'use client';

import ErrorMessage from '@/components/atoms/ErrorMessage';
import SuccessMessage from '@/components/atoms/SuccessMessage';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { useRecaptcha } from '@/hooks/useRecaptcha';
import type { FormSubmitResult, FreeformConfig, FreeformField } from '@/types/freeform';
import type { GetFreeformFormQuery } from '@/types/graphql';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import type { Control } from 'react-hook-form';
import { z } from 'zod';

import CheckboxInput from './components/CheckboxInput';
import FileInput from './components/FileInput';
import RadioGroupInput from './components/RadioGroupInput';
import SelectInput from './components/SelectInput';
// Import modular components
import TextInput from './components/TextInput';
import TextareaInput from './components/TextareaInput';

interface FormProps {
    formData: GetFreeformFormQuery;
    title?: string;
    description?: string;
}

// Extract clean error message from GraphQL error response
function extractErrorMessage(errorDetails: string): string {
    try {
        // Try to parse as JSON
        const parsed = JSON.parse(errorDetails);

        // Check if it's a GraphQL response with errors
        if (parsed.response?.errors?.[0]?.message) {
            let message = parsed.response.errors[0].message;

            // If the message is a JSON string with nested arrays, parse it recursively
            while (
                typeof message === 'string' &&
                (message.startsWith('[') || message.startsWith('{'))
            ) {
                try {
                    const innerParsed = JSON.parse(message);

                    // If it's an array, get the first element
                    if (Array.isArray(innerParsed)) {
                        if (innerParsed.length === 0) break;
                        message = innerParsed[0];
                    } else {
                        // If it's an object, we can't go deeper
                        break;
                    }
                } catch {
                    // If parsing fails, we've reached the actual message
                    break;
                }
            }

            return message;
        }

        // If it's just an array of messages, extract recursively
        if (Array.isArray(parsed)) {
            let current = parsed;
            while (Array.isArray(current) && current.length > 0) {
                current = current[0];
            }
            return typeof current === 'string' ? current : errorDetails;
        }

        return errorDetails;
    } catch {
        // Last resort: use regex to extract the message
        const match = errorDetails.match(/\[\["([^"]+)"\]\]/);
        if (match) {
            return match[1];
        }

        return errorDetails;
    }
}

// Create dynamic schema based on form fields
function createFormSchema(fields: FreeformField[]) {
    const schemaFields: Record<string, z.ZodType> = {};

    for (const field of fields) {
        if (!field?.handle) continue;

        let fieldSchema: z.ZodType = z.string();

        if (field.required) {
            fieldSchema = z.string().min(1, `${field.label || field.handle} is required`);
        } else {
            fieldSchema = z.string().optional();
        }

        // Add specific validation based on field type
        if (field.type === 'email') {
            fieldSchema = field.required
                ? z
                      .string()
                      .email('Please enter a valid email address')
                      .min(1, `${field.label || field.handle} is required`)
                : z.string().email('Please enter a valid email address').optional();
        }

        if (field.type === 'number') {
            fieldSchema = field.required
                ? z.number({ required_error: `${field.label || field.handle} is required` })
                : z.number().optional();
        }

        if (field.type === 'checkbox') {
            fieldSchema = z.boolean().optional();
        }

        if (field.type === 'checkboxes') {
            fieldSchema = z.array(z.string()).optional();
        }

        if (field.type === 'file') {
            fieldSchema = z.any().optional();
        }

        schemaFields[field.handle] = fieldSchema;
    }

    return z.object(schemaFields);
}

// Render different field types using modular components
function renderField(field: FreeformField, control: Control<Record<string, unknown>>) {
    if (!field?.handle) return null;

    const fieldProps = {
        handle: field.handle,
        label: field.label || undefined,
        instructions: field.instructions || undefined,
    };

    switch (field.type) {
        case 'text':
        case 'email':
        case 'password':
            return (
                <TextInput
                    key={field.handle}
                    field={{
                        ...fieldProps,
                        type: field.type,
                        placeholder: field.placeholder || undefined,
                        maxLength: field.maxLength || undefined,
                    }}
                    control={control}
                />
            );

        case 'textarea':
            return (
                <TextareaInput
                    key={field.handle}
                    field={{
                        ...fieldProps,
                        placeholder: field.placeholder || undefined,
                        rows: field.rows || undefined,
                    }}
                    control={control}
                />
            );

        case 'dropdown':
            return (
                <SelectInput
                    key={field.handle}
                    field={{
                        ...fieldProps,
                        options: field.options || undefined,
                    }}
                    control={control}
                />
            );

        case 'checkbox':
            return <CheckboxInput key={field.handle} field={fieldProps} control={control} />;

        case 'checkboxes':
        case 'radios':
            return (
                <RadioGroupInput
                    key={field.handle}
                    field={{
                        ...fieldProps,
                        type: field.type,
                        options: field.options || undefined,
                    }}
                    control={control}
                />
            );

        case 'file':
            return (
                <FileInput
                    key={field.handle}
                    field={{
                        ...fieldProps,
                        fileKinds: field.fileKinds || undefined,
                        maxFileSizeKB: field.maxFileSizeKB || undefined,
                    }}
                    control={control}
                />
            );

        case 'hidden':
            return (
                <input
                    key={field.handle}
                    type="hidden"
                    name={field.handle}
                    value={field.value || ''}
                />
            );

        default:
            return (
                <TextInput
                    key={field.handle}
                    field={{
                        ...fieldProps,
                        type: 'text',
                        placeholder: field.placeholder || undefined,
                    }}
                    control={control}
                />
            );
    }
}

export default function FormComponent({ formData }: FormProps) {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitResult, setSubmitResult] = useState<FormSubmitResult | null>(null);
    const [currentPageIndex, setCurrentPageIndex] = useState(0);

    const formConfig = formData?.freeform?.form;
    const pages = formConfig?.pages || [];
    const currentPage = pages[currentPageIndex];
    const isLastPage = currentPageIndex === pages.length - 1;
    const isFirstPage = currentPageIndex === 0;

    // Get reCAPTCHA v3 configuration if enabled
    const recaptchaV3 = formConfig?.captchas?.find((captcha) => captcha?.version === 'v3');
    const { executeRecaptcha, isLoaded: isRecaptchaLoaded } = useRecaptcha();

    const allFields = (
        formConfig?.pages?.flatMap(
            (page) =>
                page?.rows?.flatMap((row) => row?.fields?.filter((field) => field != null) || []) ||
                []
        ) || []
    ).filter((field): field is FreeformField => field != null);

    // Create dynamic form schema
    const formSchema = createFormSchema(allFields);
    type FormData = z.infer<typeof formSchema>;

    const form = useForm<FormData>({
        resolver: zodResolver(formSchema),
        defaultValues: allFields.reduce(
            (acc: Record<string, unknown>, field: FreeformField) => {
                if (field?.handle) {
                    if (field.type === 'checkbox') {
                        acc[field.handle] = false;
                    } else if (field.type === 'checkboxes') {
                        acc[field.handle] = [];
                    } else {
                        acc[field.handle] = '';
                    }
                }
                return acc;
            },
            {} as Record<string, unknown>
        ),
    });

    const handleNextPage = () => {
        if (!isLastPage) {
            setCurrentPageIndex((prev) => prev + 1);
        }
    };

    const handlePreviousPage = () => {
        if (!isFirstPage) {
            setCurrentPageIndex((prev) => prev - 1);
        }
    };

    const handleSubmit = async (data: FormData) => {
        if (!formConfig) return;

        // If not the last page, just go to next page
        if (!isLastPage) {
            handleNextPage();
            return;
        }

        // Final submission on last page
        setIsSubmitting(true);
        setSubmitResult(null);

        try {
            // Execute reCAPTCHA v3 if enabled
            let recaptchaToken: string | null = null;
            if (recaptchaV3?.name) {
                if (!isRecaptchaLoaded) {
                    setSubmitResult({
                        success: false,
                        message: 'reCAPTCHA is still loading. Please try again.',
                    });
                    setIsSubmitting(false);
                    return;
                }

                const action = recaptchaV3.action || 'submit';
                recaptchaToken = await executeRecaptcha(action);

                if (!recaptchaToken) {
                    setSubmitResult({
                        success: false,
                        message: 'Failed to verify reCAPTCHA. Please try again.',
                    });
                    setIsSubmitting(false);
                    return;
                }
            }

            // Prepare field information for the API
            const fields = allFields
                .filter((field) => field?.handle && field.handle !== 'submit')
                .map((field) => ({
                    handle: field.handle,
                    required: field.required || false,
                }));

            // Add reCAPTCHA token to form data if available
            const submissionData = { ...data };
            if (recaptchaToken && recaptchaV3?.name) {
                submissionData[recaptchaV3.name] = recaptchaToken;
            }

            // Use form properties from the already-fetched form data
            const response = await fetch('/api/freeform/submit', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    formHandle: formConfig.handle,
                    formData: submissionData,
                    csrfToken: formConfig.csrfToken?.value || '',
                    fields,
                }),
            });

            const result = await response.json();

            // The response structure is now save_${formHandle}_Submission
            const submissionKey = `save_${formConfig.handle}_Submission`;
            const submissionResult = result[submissionKey];

            const config = formConfig as FreeformConfig; // Temporary type assertion until types are updated

            if (response.ok && submissionResult?.id) {
                // Handle success behavior
                if (config.successBehavior === 'redirect-return-url' && config.returnUrl) {
                    // Redirect to the return URL
                    window.location.href = config.returnUrl;
                    return;
                }

                // Show success message (reload behavior or no redirect configured)
                setSubmitResult({
                    success: true,
                    message: config.successMessage || 'Form submitted successfully!',
                });
                form.reset();
            } else {
                // Extract clean error message
                let errorMessage = config.errorMessage || 'Failed to submit form';
                if (result.error) {
                    errorMessage = result.error;
                }
                if (result.details) {
                    const cleanMessage = extractErrorMessage(result.details);
                    errorMessage = cleanMessage;
                }

                setSubmitResult({
                    success: false,
                    message: errorMessage,
                });
            }
        } catch {
            setSubmitResult({
                success: false,
                message: 'An error occurred while submitting the form',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!formConfig) {
        return <ErrorMessage message="Form configuration not available" />;
    }

    return (
        <>
            {submitResult && (
                <div className="mb-6">
                    {submitResult.success ? (
                        <SuccessMessage message={submitResult.message || 'Success!'} />
                    ) : (
                        <div>
                            <ErrorMessage message={submitResult.message || 'Error occurred'} />
                            {submitResult.errors && submitResult.errors.length > 0 && (
                                <div className="mt-2">
                                    {submitResult.errors.map((error) => (
                                        <p key={error.field} className="text-red-600 text-sm">
                                            {error.field}: {error.message}
                                        </p>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Page indicator for multi-page forms */}
            {pages.length > 1 && (
                <div className="mb-6">
                    <div className="flex items-center justify-between text-gray-600 text-sm">
                        <span>
                            Page {currentPageIndex + 1} of {pages.length}
                        </span>
                        <div className="flex space-x-2">
                            {pages.map((page, index) => (
                                <div
                                    key={page?.index ?? `page-${index}`}
                                    className={`h-3 w-3 rounded-full ${
                                        index === currentPageIndex
                                            ? 'bg-haug-choco-100'
                                            : index < currentPageIndex
                                              ? 'bg-haug-choco-70'
                                              : 'bg-gray-300'
                                    }`}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            )}

            <Form {...form}>
                <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
                    {/* Show current page only */}
                    {currentPage && (
                        <div>
                            {currentPage.label && pages.length > 1 && (
                                <h3 className="mb-4 font-neue-haas font-semibold text-lg">
                                    {currentPage.label}
                                </h3>
                            )}
                            {currentPage.rows?.map((row, rowIndex) => (
                                <div key={row?.id ?? `row-${rowIndex}`}>
                                    {row?.fields?.map((field, fieldIndex) => (
                                        <div
                                            key={field?.id ?? `field-${fieldIndex}`}
                                            className="mb-4"
                                        >
                                            {field &&
                                                renderField(field as FreeformField, form.control)}
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Navigation buttons */}
                    <div className="flex gap-4">
                        {/* Back button */}
                        {!isFirstPage && currentPage?.buttons?.back && (
                            <Button
                                type="button"
                                variant="outline"
                                onClick={handlePreviousPage}
                                className="flex-1"
                            >
                                {currentPage.buttons.backLabel || 'Back'}
                            </Button>
                        )}

                        {/* Next/Submit button */}
                        <Button
                            type="submit"
                            disabled={isSubmitting}
                            className={
                                !isFirstPage && currentPage?.buttons?.back ? 'flex-1' : 'w-full'
                            }
                        >
                            {isSubmitting
                                ? 'Submitting...'
                                : currentPage?.buttons?.submitLabel ||
                                  (isLastPage ? 'Submit' : 'Next')}
                        </Button>
                    </div>

                    {/* reCAPTCHA privacy notice (required when hiding badge) */}
                    {recaptchaV3 && (
                        <p className="mt-4 text-gray-600 text-sm">
                            This site is protected by reCAPTCHA and the Google{' '}
                            <a
                                href="https://policies.google.com/privacy"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="underline hover:text-haug-choco-100"
                            >
                                Privacy Policy
                            </a>{' '}
                            and{' '}
                            <a
                                href="https://policies.google.com/terms"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="underline hover:text-haug-choco-100"
                            >
                                Terms of Service
                            </a>{' '}
                            apply.
                        </p>
                    )}
                </form>
            </Form>
        </>
    );
}
